import { Box } from "@mui/material";
import { IconCheckCircle } from "src/components/icons-mui/check-circle";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";

const CERTIFICATE_BOX_PROGRESS_BAR_TEST_ID = "certificate-box-progress-bar";

interface IProps {
    prefix?: string;
    currentValue: number;
    requiredValue: number;
    description: string;
}

export const CBProgressBar = ({ prefix, currentValue, requiredValue, description }: IProps) => {
    const { formatNumber } = useNumberFormatter();

    const percentage = currentValue < requiredValue ? Math.round((100 * currentValue) / requiredValue) : 100;
    const isCompleted = percentage === 100;

    return (
        <Box
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${description}: ${formatNumber(currentValue)} of ${formatNumber(requiredValue)}${
                prefix ? ` ${prefix}` : ""
            } (${percentage}% complete)`}
            sx={{
                position: "relative",
                width: "100%",
                bgcolor: "gray.100",
            }}
            data-test-id={CERTIFICATE_BOX_PROGRESS_BAR_TEST_ID}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${percentage}%`,
                    bgcolor: "green.200",
                    transition: "width 0.4s",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            />
            <Box
                sx={{
                    width: "100%",
                    fontSize: "xs",
                    py: 1,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                }}
            >
                <Box sx={{ mr: 1 }}>
                    {isCompleted ? (
                        <IconCheckCircle fill="white" sx={{ width: "20px", height: "20px", m: "2px" }} />
                    ) : (
                        <Box
                            sx={{
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                m: "2px",
                                border: "2px solid",
                                borderColor: "white",
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: "block", alignItems: "center", wordBreak: "break-word" }}>
                    <strong>
                        {prefix ? `${prefix} ` : ""}
                        {formatNumber(currentValue)} / {formatNumber(requiredValue)}
                    </strong>
                    &nbsp;{description}
                </Box>
            </Box>
        </Box>
    );
};
